import { Dispatch } from 'redux';
import { TGetRootState } from 'types/shared';
import { RSAA, ApiError } from 'redux-api-middleware';
import { RootState } from 'redux/rootReducer';

export const REQUEST = 'opus/mylists/REQUEST';
export const FAILURE = 'opus/mylists/FAILURE';
export const SUCCESS = 'opus/mylists/SUCCESS';

export type MyListsActionTypes =
  | typeof REQUEST
  | typeof SUCCESS
  | typeof FAILURE;

type IMyListsAction = {
  type: MyListsActionTypes;
  payload?: string[];
  error?: any;
};

type TLoadingState = 'loading' | 'success' | 'error' | 'initial';

interface IMyListsState {
  loadingState: TLoadingState;
  errorInfo?: any;
  data?: string[];
}

export const initialMyListsState: IMyListsState = {
  loadingState: 'initial',
};

export default function reducer(
  state: IMyListsState = initialMyListsState,
  action: IMyListsAction
): IMyListsState {
  switch (action.type) {
    case REQUEST: {
      return {
        ...state,
        loadingState: 'loading',
      };
    }
    case SUCCESS: {
      return {
        ...state,
        loadingState: 'success',
        data: Array.isArray(action.payload) ? action.payload : [],
      };
    }
    case FAILURE: {
      return {
        ...state,
        loadingState: 'error',
        errorInfo: action.error,
      };
    }
    default:
      return state;
  }
}

export const isInMyLists = (
  listId: string
): ((rootState: RootState) => boolean) => {
  return (rootState: RootState): boolean => {
    return rootState.myLists.data?.includes(listId) ?? false;
  };
};

export const shouldLoadLists = () => {
  return (_dispatch: Dispatch, getState: TGetRootState): boolean => {
    return (
      getState().myLists.loadingState === 'initial' ||
      getState().myLists.loadingState === 'error'
    );
  };
};

export const addToMyLists = (
  listId: string
): ((dispatch: Dispatch, getState: TGetRootState) => Promise<any>) => {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const lists = getState().myLists.data ?? [];
    if (lists.includes(listId)) {
      return null;
    }
    const newList = [...lists, listId];
    const { id } = getState().authentication.profile;

    const actionResponse = await dispatch({
      [RSAA]: {
        types: [
          REQUEST,
          {
            type: SUCCESS,
            payload: newList,
          },
          FAILURE,
        ],
        endpoint: getMyListsEndpoint(id),
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(newList),
      },
    });

    return actionResponse;
  };
};

export const removeFromMyLists = (
  listIds: string[]
): ((dispatch: Dispatch, getState: TGetRootState) => Promise<any>) => {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const lists = getState().myLists.data ?? [];
    const idsToBeDeleted = listIds.filter((id: string): boolean => {
      return lists.indexOf(id) > -1;
    });
    if (!idsToBeDeleted.length) {
      return null;
    }

    const newList = lists.filter(
      (id: string): boolean => !idsToBeDeleted.includes(id)
    );

    const { id } = getState().authentication.profile;

    const actionResponse = await dispatch({
      [RSAA]: {
        types: [
          REQUEST,
          {
            type: SUCCESS,
            payload: newList,
          },
          FAILURE,
        ],
        endpoint: getMyListsEndpoint(id),
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(newList),
      },
    });

    return actionResponse;
  };
};

export const loadMyLists = (): ((
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any>) => {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;

    const actionResponse = await dispatch({
      [RSAA]: {
        types: [
          REQUEST,
          SUCCESS,
          {
            type: FAILURE,
            meta: (_action, _state, res: any): void => {
              if (res?.status === 404) {
                loadDefaultsIf404()(dispatch, getState);
              }
            },
          },
        ],
        endpoint: getMyListsEndpoint(id),
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });

    if (actionResponse.error) {
      if ((actionResponse.payload as ApiError<any>).status === 404) {
        return await dispatch(initMyLists() as any);
      }
    }

    // if DB return {}, lets update db to valid value []
    if (
      !actionResponse.error &&
      actionResponse.payload &&
      !Array.isArray(actionResponse.payload)
    ) {
      return await dispatch(initMyLists() as any);
    }

    return actionResponse;
  };
};

function initMyLists(): (
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any> {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;

    // dispatch the action
    return await dispatch({
      [RSAA]: {
        types: [
          REQUEST,
          {
            type: SUCCESS,
            payload: [],
          },
          FAILURE,
        ],
        endpoint: getMyListsEndpoint(id),
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify([]),
      },
    });
  };
}

const loadDefaultsIf404 =
  (): any =>
  (dispatch: Dispatch, getState: TGetRootState): any => {
    // get the user id
    const { id } = getState().authentication.profile;

    // fetch the users' custom regions
    return dispatch({
      [RSAA]: {
        types: [
          REQUEST,
          {
            type: SUCCESS,
            payload: [],
          },
          FAILURE,
        ],
        endpoint: getMyListsEndpoint(id),
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify([]),
      },
    });
  };

const getMyListsEndpoint = (id: string): string => {
  return `/kv/user_${id}_my_custom_lists`;
};
